
import React, { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Lead, LeadStatus, leadStatusLabels } from "./types";
import { 
  ResponsiveContainer, 
  FunnelChart, 
  Funnel, 
  LabelList, 
  Tooltip
} from 'recharts';

interface LeadFunnelChartProps {
  leads: Lead[];
}

export const LeadFunnelChart: React.FC<LeadFunnelChartProps> = ({ leads }) => {
  const getFunnelStageColor = (stage: LeadStatus): string => {
    switch (stage) {
      case 'new': return '#94a3b8'; // gray
      case 'contacted': return '#60a5fa'; // blue
      case 'meeting': return '#a78bfa'; // purple
      case 'proposal': return '#fbbf24'; // amber
      case 'negotiating': return '#f97316'; // orange
      case 'won': return '#22c55e'; // green
      default: return '#94a3b8'; // gray
    }
  };

  const funnelData = useMemo(() => {
    // Define the funnel stages in order
    const stages: LeadStatus[] = ['new', 'contacted', 'meeting', 'proposal', 'negotiating', 'won'];

    // Count leads in each stage
    const stageCounts: {[key in LeadStatus]?: number} = {};
    stages.forEach(stage => {
      stageCounts[stage] = leads.filter(lead => lead.status === stage).length;
    });

    // Calculate conversion rates
    const totalLeads = leads.length;
    let previousCount = totalLeads;
    
    // Create funnel data with conversion rates
    return stages.map(stage => {
      const count = stageCounts[stage] || 0;
      const conversionRate = previousCount ? Math.round((count / previousCount) * 100) : 0;
      previousCount = count;
      
      return {
        name: leadStatusLabels[stage] || stage,
        value: count,
        conversionRate: conversionRate,
        fill: getFunnelStageColor(stage)
      };
    }).filter(item => item.value > 0); // Only show stages with leads
  }, [leads]);

  if (funnelData.length === 0) {
    return (
      <Card className="w-full h-[400px]">
        <CardHeader>
          <CardTitle>Lead Conversion Funnel</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-muted-foreground">No lead data available to display funnel visualization</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full h-[400px]">
      <CardHeader>
        <CardTitle>Lead Conversion Funnel</CardTitle>
      </CardHeader>
      <CardContent className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <FunnelChart>
            <Tooltip
              formatter={(value, name, props) => [
                `Count: ${value} leads`, 
                `${props.payload.name}`
              ]}
            />
            <Funnel
              dataKey="value"
              data={funnelData}
              isAnimationActive
            >
              <LabelList
                position="right"
                fill="#888"
                stroke="none"
                dataKey="name"
              />
              <LabelList
                position="right"
                fill="#888"
                stroke="none"
                dataKey="conversionRate"
                formatter={(value) => `${value}%`}
                offset={50}
              />
            </Funnel>
          </FunnelChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
